import { Stack, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GqlPlannerV2Visit, GqlPlannerVisitStatus } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import useQueryStringGridModels from "~/modules/app/hooks/useQueryStringGridModels";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { convertParamsToQueryOptions } from "~/modules/app/util/filterAndPaginationUtils";
import { formatDateTime } from "~/modules/app/util/formatters";
import VisitStatusChip from "~/shared/components/VisitStatusChip";

import { useProjectVisitListGetAll } from "../../api/useProjectVisitListGetAll";

const defaultParams = {
    page: 0,
    pageSize: 50,
    sortBy: "startTime",
    sortOrder: "asc",
};

export function ProjectVisitListScreen() {
    const { t } = useTranslation("planner");

    const { filterModel, onFilterModelChange, gridModels, gqlVariables } = useQueryStringGridModels(
        convertParamsToQueryOptions,
        defaultParams,
    );

    const { data, loading, error, filterOptions, total } = useProjectVisitListGetAll(gqlVariables.params);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "plannerArea.name",
                headerName: t("visitListScreen.areaName"),
                valueGetter: (_, row) => row.plannerArea?.name,
                flex: 1,
            },
            {
                field: "protocol.name",
                headerName: t("visitListScreen.protocol"),

                renderCell: ({ row }) => (
                    <Stack>
                        <Typography variant="body1">{row.protocol?.name}</Typography>
                        <Typography variant="caption">
                            {row.protocolVisit?.name ?? t("visitListScreen.visitName")} {row.protocolVisit?.number ?? ""}
                        </Typography>
                    </Stack>
                ),

                flex: 1,
            },
            {
                field: "requiredFieldworkers",
                headerName: t("visitListScreen.requiredWorkers"),
                valueGetter: (_, row) => row.requiredFieldworkers,
                flex: 1,
            },
            {
                field: "startTime",
                headerName: t("visitListScreen.startTime"),
                valueGetter: (_, row) =>
                    row.status !== GqlPlannerVisitStatus.Unplanned && row.startTime !== null
                        ? formatDateTime(parseStringToDate(row.startTime))
                        : null,
                flex: 1,
            },
            {
                field: "status",
                headerName: t("visitListScreen.status"),
                renderCell: ({ row }: GridRenderCellParams<GqlPlannerV2Visit>) => <VisitStatusChip value={row.status} />,
                flex: 1,
            },
            {
                type: "actions",
                field: "",
                maxWidth: 40,
                valueGetter: (_, visit) => {
                    if (visit.status !== GqlPlannerVisitStatus.Approved) return;

                    const clientSlug = visit.plannerArea.project.client.slug;
                    const projectSlug = visit.plannerArea.project.slug;

                    return `/c/${clientSlug}/p/${projectSlug}/visits/${visit.id}`;
                },
                renderCell: ({ value }) => (value !== undefined ? <GaiaTableLinkCell value={value} /> : undefined),
            },
        ],
        [t],
    );

    if (!data && !loading && error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent title={t("visitListScreen.title")}>
                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    filterOptions={filterOptions}
                    totalRows={total || 0}
                    columns={columns}
                    filterModel={filterModel}
                    onFilterModelChange={onFilterModelChange}
                    gridModels={gridModels}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
